/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
// import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Autocomplete, RenderInputParams } from '@material-ui/lab';
import { TextField, CircularProgress, PopperProps } from '@material-ui/core';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { FetchPolicy } from 'apollo-client';
import gql from 'graphql-tag';
import { client } from '../..';
import { IAutoCompleteItem } from '../../typings/autoComplete';
import { showNotification } from '../../App';
import { EmailTemplateMessageTypeEnum, EmailTemplateTypeEnum } from '../../emailTemplatesConfiguration/EmailTemplateRespository';

export interface EmailTemplateQueryParams {
    emailType: String;
    areaOfLaw: String;
    isActive: boolean;
    messageType?: EmailTemplateMessageTypeEnum;
}

interface EmailTemplateSelectorProps {
    disabled?: boolean;
    required?: boolean;
    multiple?: boolean;
    // tslint:disable-next-line: no-any
    value?: IAutoCompleteItem | any; // value only available in single select
    error?: boolean;
    onSelection?: (value?: IAutoCompleteItem | IAutoCompleteItem[], name?: string) => void;
    onBlur?: React.FocusEventHandler<HTMLDivElement>;
    className?: string;
    helperText?: string;
    name?: string;
    label?: string;
    style?: React.CSSProperties; 
    disablePortal?: boolean;
    PopperComponent?: React.ComponentType<PopperProps>;
    areaOfLaw: string;
    emailType: EmailTemplateTypeEnum;
    messageType?: EmailTemplateMessageTypeEnum;
    variant?: 'standard' | 'outlined' | 'filled';
    size?: 'small' | 'medium';
}

interface EmailTemplateSelectorState {
    isLoading: boolean;
    data: IAutoCompleteItem[];
}

export const EmailTemplateSelector: React.FC<EmailTemplateSelectorProps> = props => {

    // const classes = useStyles();

    const [state, setState] = useState<EmailTemplateSelectorState>({
        isLoading: false,
        data: [],
    });

    useEffect(() => {
        let isMounted = true;

        fetchData(isMounted);

        return () => {
            isMounted = false;
        };
    // tslint:disable-next-line: align
    }, []);

    const fetchData = (isMounted: boolean) => {

        setState({
            ...state,
            isLoading: true
        });

        let query: EmailTemplateQueryParams = {
            areaOfLaw: props.areaOfLaw,
            emailType: EmailTemplateTypeEnum[props.emailType],
            isActive: true,
            messageType: props.messageType
        };

        retrieveEmailTemplateData(
            query,
            false, // this.state.isRefresh, -- Since we are updating and creating, we do not fetch cached data
            // tslint:disable-next-line: no-console
            (data) => {
                if (!isMounted) { return; }
                onRetrieveData(data);
            },
            // tslint:disable-next-line:no-any
            function (reason: any): void {
                if (!isMounted) { return; }
                showNotification(null, reason, 'error');
            }
        );
    };

    const onRetrieveData = (data: TemplatedData) => {
        setState({
            ...state,
            data: data.templates.emailTemplateList.map((source: EmailTemplate) => ({
                value: source.emailTemplateGuid,
                label: source.name,
                secondaryValue: source.emailType
            })),
            isLoading: false
        });
    };

    // tslint:disable-next-line: no-any
    const handleChange = (event: React.ChangeEvent<{}>, value: any) => {
        if (props.onSelection) {
            props.onSelection(value, props.name);
        }
    };

    const getInputProps = (params: RenderInputParams, isLoading?: boolean) => {
        return  ({   
            ...params.InputProps,
            endAdornment: (
                <React.Fragment>
                  {isLoading ? <CircularProgress color="primary" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),     
         });
    };

    return (
        <>
            { props.multiple ? 
                (
                    <Autocomplete
                        style={props.style}
                        className={props.className}
                        // [] (not null) is MUI's "nothing selected" sentinel for multiple={true}.
                        value={Array.isArray(props.value) ? props.value : []}
                        multiple={true}
                        loading={state.isLoading}
                        disabled={props.disabled}
                        onChange={handleChange}
                        onBlur={props.onBlur}
                        options={state.data}
                        // tslint:disable-next-line: max-line-length
                        disablePortal={props.disablePortal !== undefined ? props.disablePortal : true} // to Stop the dropdown select from closing the form
                        // tslint:disable-next-line: jsx-no-lambda
                        getOptionLabel={(option: IAutoCompleteItem) => option.label}
                        getOptionSelected={(option: IAutoCompleteItem, value: IAutoCompleteItem) => option.value === value.value}
                        PopperComponent={props.PopperComponent}
                        ChipProps={{
                            size: props.size || 'medium'
                        }}       
                        // tslint:disable-next-line: jsx-no-lambda
                        renderInput={params => (
                            <TextField 
                                {...params} 
                                label={props.label}
                                variant={props.variant || "standard"} 
                                fullWidth={true} 
                                margin="none" 
                                required={props.required}
                                InputProps={getInputProps(params, state.isLoading)}
                                error={props.error}      
                                helperText={props.helperText}  
                                size={props.size}                    
                            />
                        )}
                        // tslint:disable-next-line: jsx-no-lambda
                        renderOption={(option, { inputValue }) => {
                            const matches = match(option.label, inputValue);
                            const parts = parse(option.label, matches);
                    
                            return (
                                <div>
                                    {parts.map((part, index) => (
                                        <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                                            {part.text}
                                        </span>
                                    ))}
                                </div>
                            );
                        }}
                    />
                )
                :
                (
                    <Autocomplete
                        style={props.style}
                        className={props.className}
                        // Explicit bug report: the raw field value (e.g. '' before anything is
                        // selected) was passed straight through - not an {label,value}-shaped
                        // object matching `options` - causing MUI's getOptionLabel to be called
                        // on a non-object and log "getOptionLabel returned undefined...". null
                        // is MUI's real "nothing selected" sentinel.
                        value={props.value && typeof props.value === 'object' ? props.value : null}
                        loading={state.isLoading}
                        disabled={props.disabled}
                        onChange={handleChange}
                        onBlur={props.onBlur}
                        options={state.data}
                        // tslint:disable-next-line: max-line-length
                        disablePortal={props.disablePortal !== undefined ? props.disablePortal : true} // to Stop the dropdown select from closing the form
                        // tslint:disable-next-line: jsx-no-lambda
                        getOptionLabel={(option: IAutoCompleteItem) => option.label}
                        getOptionSelected={(option: IAutoCompleteItem, value: IAutoCompleteItem) => option.value === value.value}
                        // tslint:disable-next-line: jsx-no-lambda
                        PopperComponent={props.PopperComponent}
                        ChipProps={{
                            size: props.size || 'medium'
                        }}         
                        renderInput={params => (
                            <TextField 
                                {...params} 
                                label={props.label}
                                variant={props.variant || "standard"} 
                                fullWidth={true} 
                                margin="none" 
                                required={props.required}
                                InputProps={getInputProps(params, state.isLoading)}
                                error={props.error}      
                                helperText={props.helperText}
                                size={props.size}                      
                            />
                        )}
                        // tslint:disable-next-line: jsx-no-lambda
                        renderOption={(option, { inputValue }) => {
                            const matches = match(option.label, inputValue);
                            const parts = parse(option.label, matches);
                    
                            return (
                                <div>
                                {parts.map((part, index) => (
                                    <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                                    {part.text}
                                    </span>
                                ))}
                                </div>
                            );
                        }}
                    />
                )
            }
        </>
    );
};

export interface TemplatedData {
    templates: Templates;
}
export interface Templates {
    emailTemplateList: EmailTemplate[];
}
export interface EmailTemplate {
    id: number;
    emailTemplateGuid: string;
    emailStyle: number;
    emailType: number;
    name: string;
    description: boolean;
}

export function retrieveEmailTemplateData(
    query: EmailTemplateQueryParams,
    refreshData: boolean,
    onSuccess: (data: TemplatedData) => void,
    // tslint:disable-next-line:no-any
    onError: (reason: any) => void
): void {
    var fetchPolicy: FetchPolicy = refreshData === true ? 'network-only' : 'cache-first';
    client
        .query({
            query: EmailTemplateListData,
            variables: {
                emailType: query.emailType,
                areaOfLaw: query.areaOfLaw,
                isActive: query.isActive,
                messageType: query.messageType
            },
            fetchPolicy: fetchPolicy,
        })
        // tslint:disable-next-line:no-any
        .then((results: { data: any }) => {
            onSuccess(results.data);
        })
        // tslint:disable-next-line:no-any
        .catch((reason: any) => {
            onError(reason);
        });
}

// A follow-up action's default template is only used when the selector itself would offer it (active, right message type and
// area of law): a deactivated or out-of-area default would otherwise open a dialog that cannot load it. It is the same query the
// selector runs, so it comes from the cache once the selector has loaded.
export function retrieveDefaultFollowUpTemplate(
    defaultTemplate: IAutoCompleteItem,
    areaOfLaw: string,
    messageType: EmailTemplateMessageTypeEnum,
    onFound: (template: IAutoCompleteItem) => void,
): void {
    retrieveEmailTemplateData(
        {
            areaOfLaw,
            emailType: EmailTemplateTypeEnum[EmailTemplateTypeEnum.EMAIL_RESPONSE],
            isActive: true,
            messageType,
        },
        false,
        (data) => {
            const matchedTemplate = data.templates.emailTemplateList.find(
                (template: EmailTemplate) => template.emailTemplateGuid.toLowerCase() === defaultTemplate.value.toLowerCase(),
            );
            if (matchedTemplate) {
                onFound({ value: matchedTemplate.emailTemplateGuid, label: matchedTemplate.name, secondaryValue: matchedTemplate.emailType });
            }
        },
        // the selector reports a failed load itself; without a default the user picks the template as before
        () => undefined,
    );
}

const EmailTemplateListData = gql`
    query EmailTemplateList(
        $emailType: EmailType, 
        $areaOfLaw: String, 
        $isActive: Boolean
        $messageType: MessageType
    ) {
        templates {
            emailTemplateList(
                emailType:$emailType, 
                areaOfLaw: $areaOfLaw, 
                isActive: $isActive,
                messageType: $messageType
            ){
                emailTemplateGuid
                emailType,
                emailStyle,
                name,
                description
            }
        }
    }
`;